"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { FiUsers, FiStar, FiShield, FiUserX } from "react-icons/fi"
import api from "../../../libs/axios"
import toast from "react-hot-toast"
import ModalSelect from "../../../ui/select-modal"

interface Player {
  player_id: number
  player_name: string
  player_role: number
  jersey_number: number
  is_captain?: boolean
  is_wicket_keeper?: boolean
  is_12th_man?: boolean
}

interface Props {
  onComplete: (data: any) => void
  onBack: () => void
  matchData: any
}

export default function SelectPlayers({ onComplete, onBack, matchData }: Props) {
  const [formData, setFormData] = useState({
    team_a_id: matchData.team_a_id?.toString() || "",
    team_b_id: matchData.team_b_id?.toString() || "",
    team_a_players: matchData.team_a_players || [],
    team_b_players: matchData.team_b_players || [],
  })

  const [errors, setErrors] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [teamAPlayers, setTeamAPlayers] = useState<any[]>([])
  const [teamBPlayers, setTeamBPlayers] = useState<any[]>([])

  console.log("Match Data in SelectPlayers:", matchData)
  useEffect(() => {
    if (!matchData) return
    setFormData((prev) => ({
      ...prev,
      team_a_id: matchData.team_a_id != null ? matchData.team_a_id.toString() : prev.team_a_id,
      team_b_id: matchData.team_b_id != null ? matchData.team_b_id.toString() : prev.team_b_id,
      team_a_players: matchData.team_a_players ?? prev.team_a_players,
      team_b_players: matchData.team_b_players ?? prev.team_b_players,
    }))
  }, [matchData])

  useEffect(() => {
    if (formData.team_a_id) {
      fetchTeamPlayers(formData.team_a_id, setTeamAPlayers)
    }
  }, [formData.team_a_id])

  useEffect(() => {
    if (formData.team_b_id) {
      fetchTeamPlayers(formData.team_b_id, setTeamBPlayers)
    }
  }, [formData.team_b_id])

  // Fetch teams with pagination and prioritize selected teams
  const fetchTeams = async (page: number, search?: string, limit = 10, priorityTeamIds?: string[]) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search ? { search } : {}),
        // Add team_ids to prioritize selected teams
        ...(priorityTeamIds && priorityTeamIds.length > 0 ? { team_ids: priorityTeamIds.join(',') } : {}),
      })

      const response = await api.get(`/teams?${params}`)

      const teamsData = response.data?.data || []
      const paginationData = response.data?.pagination

      const data = teamsData.map((team: any) => ({
        value: team.id.toString(),
        label: team.name,
        description: team.location || "Cricket team",
        data: team,
      }))

      const pagination = {
        current_page: paginationData?.current_page || page,
        per_page: paginationData?.per_page || limit,
        total: paginationData?.total || 0,
        total_pages: paginationData?.total_pages || Math.ceil((paginationData?.total || 0) / limit),
        has_next_page: paginationData?.has_next_page || false,
        has_prev_page: paginationData?.has_prev_page || page > 1,
        next_page: paginationData?.next_page || null,
        prev_page: paginationData?.prev_page || null,
      }

      return { data, pagination }
    } catch (error: any) {
      console.error("Error fetching teams:", error)
      throw error
    }
  }

  // Fetch teams for Team A
  const fetchTeamsForTeamA = async (page: number, search?: string, limit = 10) => {
    // Build priority array with selected teams
    const priorityTeamIds: string[] = []
    if (formData.team_a_id) priorityTeamIds.push(formData.team_a_id)
    if (formData.team_b_id) priorityTeamIds.push(formData.team_b_id)

    const result = await fetchTeams(page, search, limit, priorityTeamIds)

    // Filter out Team B if selected
    if (formData.team_b_id) {
      result.data = result.data.filter((team: any) => team.value !== formData.team_b_id.toString())
    }

    return result
  }

  // Fetch teams for Team B
  const fetchTeamsForTeamB = async (page: number, search?: string, limit = 10) => {
    // Build priority array with selected teams
    const priorityTeamIds: string[] = []
    if (formData.team_b_id) priorityTeamIds.push(formData.team_b_id)
    if (formData.team_a_id) priorityTeamIds.push(formData.team_a_id)

    const result = await fetchTeams(page, search, limit, priorityTeamIds)

    // Filter out Team A if selected
    if (formData.team_a_id) {
      result.data = result.data.filter((team: any) => team.value !== formData.team_a_id.toString())
    }

    return result
  }

  const fetchTeamPlayers = async (teamId: string, setPlayers: any) => {
    try {
      const response = await api.get(`/teams/${teamId}`)

      // Extract players from the nested response structure
      const players = response.data.players.data || []
      setPlayers(players)

    } catch (error) {
      console.error("Error fetching team players:", error)
      toast.error("Failed to fetch team players")
    }
  }

  const validateTeamPlayers = (players: Player[], teamName: string) => {
    const errors: string[] = []

    if (players.length < 7 || players.length > 12) {
      errors.push(`${teamName} must have 7-12 players`)
    }

    const playingPlayers = players.filter((p) => !p.is_12th_man)
    const twelfthMen = players.filter((p) => p.is_12th_man)
    const captains = players.filter((p) => p.is_captain)
    const wicketKeepers = players.filter((p) => p.is_wicket_keeper)

    if (players.length > 11) {
      if (twelfthMen.length !== 1) {
        errors.push(`${teamName} must have exactly 1 12th man when more than 11 players are selected`)
      }
      if (playingPlayers.length !== 11) {
        errors.push(`${teamName} must have exactly 11 playing players when 12th man is present`)
      }
    } else {
      if (twelfthMen.length > 0) {
        errors.push(`${teamName} cannot have a 12th man with 11 or fewer total players`)
      }
      if (playingPlayers.length < 7 || playingPlayers.length > 11) {
        errors.push(`${teamName} must have 7-11 playing players`)
      }
    }

    if (captains.length !== 1) {
      errors.push(`${teamName} must have exactly 1 captain`)
    }

    if (wicketKeepers.length !== 1) {
      errors.push(`${teamName} must have exactly 1 wicket keeper`)
    }

    return errors
  }

  const validateForm = () => {
    const newErrors: any = {}

    if (formData.team_a_players.length !== formData.team_b_players.length) {
      toast.error("Please select the same number of players for both teams")
      return false
    }

    if (!formData.team_a_id) {
      newErrors.team_a_id = "Team A is required"
    }

    if (!formData.team_b_id) {
      newErrors.team_b_id = "Team B is required"
    }

    if (formData.team_a_id === formData.team_b_id) {
      newErrors.team_b_id = "Team A and Team B cannot be the same"
    }

    const teamAErrors = validateTeamPlayers(formData.team_a_players, "Team A")
    const teamBErrors = validateTeamPlayers(formData.team_b_players, "Team B")

    if (teamAErrors.length > 0) {
      newErrors.team_a_players = teamAErrors.join(", ")
    }

    if (teamBErrors.length > 0) {
      newErrors.team_b_players = teamBErrors.join(", ")
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handlePlayerToggle = (team: "A" | "B", playerId: number) => {
    const teamKey = team === "A" ? "team_a_players" : "team_b_players"
    const currentPlayers = formData[teamKey]

    const existingIndex = currentPlayers.findIndex((p: Player) => p.player_id === playerId)

    if (existingIndex >= 0) {
      // Remove player
      const newPlayers = currentPlayers.filter((_: any, index: number) => index !== existingIndex)
      setFormData((prev) => ({ ...prev, [teamKey]: newPlayers }))
    } else {
      // Add player
      const playerData = (team === "A" ? teamAPlayers : teamBPlayers).find((p) => p.id === playerId)
      const newPlayer: Player = {
        player_id: playerId,
        player_name: playerData.player_name,
        player_role: playerData.player_role,
        jersey_number: playerData.jersey_number,
        is_captain: false,
        is_wicket_keeper: false,
        is_12th_man: false,
      }
      setFormData((prev) => ({ ...prev, [teamKey]: [...currentPlayers, newPlayer] }))
    }
  }

  const handlePlayerRoleToggle = (
    team: "A" | "B",
    playerId: number,
    role: "captain" | "wicket_keeper" | "12th_man",
  ) => {
    const teamKey = team === "A" ? "team_a_players" : "team_b_players"
    const currentPlayers = formData[teamKey]

    const updatedPlayers = currentPlayers.map((player: Player) => {
      if (player.player_id === playerId) {
        if (role === "captain") {
          return { ...player, is_captain: !player.is_captain }
        } else if (role === "wicket_keeper") {
          return { ...player, is_wicket_keeper: !player.is_wicket_keeper }
        } else if (role === "12th_man") {
          return { ...player, is_12th_man: !player.is_12th_man }
        }
      }
      // If setting captain, wicket keeper, or 12th man, remove from others
      if (role === "captain" && player.player_id !== playerId) {
        return { ...player, is_captain: false }
      }
      if (role === "wicket_keeper" && player.player_id !== playerId) {
        return { ...player, is_wicket_keeper: false }
      }
      if (role === "12th_man" && player.player_id !== playerId) {
        return { ...player, is_12th_man: false }
      }
      return player
    })

    setFormData((prev) => ({ ...prev, [teamKey]: updatedPlayers }))
  }

  // Helper functions for validation
  const getTeamStats = (selectedPlayers: Player[]) => {
    return {
      total: selectedPlayers.length,
      captains: selectedPlayers.filter((p) => p.is_captain).length,
      wicketKeepers: selectedPlayers.filter((p) => p.is_wicket_keeper).length,
      twelfthMen: selectedPlayers.filter((p) => p.is_12th_man).length,
      playingEleven: selectedPlayers.filter((p) => !p.is_12th_man).length,
    }
  }

  const isPlayerSelectionDisabled = (selectedPlayers: Player[]) => {
    return selectedPlayers.length >= 12
  }

  const isCaptainSelectionDisabled = (selectedPlayers: Player[], currentPlayerId: number) => {
    const currentPlayer = selectedPlayers.find((p) => p.player_id === currentPlayerId)
    const existingCaptain = selectedPlayers.find((p) => p.is_captain && p.player_id !== currentPlayerId)
    return !currentPlayer?.is_captain && existingCaptain !== undefined
  }

  const isWicketKeeperSelectionDisabled = (selectedPlayers: Player[], currentPlayerId: number) => {
    const currentPlayer = selectedPlayers.find((p) => p.player_id === currentPlayerId)
    const existingWicketKeeper = selectedPlayers.find((p) => p.is_wicket_keeper && p.player_id !== currentPlayerId)
    return !currentPlayer?.is_wicket_keeper && existingWicketKeeper !== undefined
  }

  // New helper function for 12th man selection
  const is12thManSelectionDisabled = (selectedPlayers: Player[], currentPlayerId: number) => {
    const currentPlayer = selectedPlayers.find((p) => p.player_id === currentPlayerId)
    const existing12thMan = selectedPlayers.find((p) => p.is_12th_man && p.player_id !== currentPlayerId)
    const hasMoreThan11Players = selectedPlayers.length > 11

    // Disable if:
    // 1. Current player is not 12th man AND another player is already 12th man
    // 2. OR if team doesn't have more than 11 players (can't have 12th man with 11 or fewer)
    return (!currentPlayer?.is_12th_man && existing12thMan !== undefined) || !hasMoreThan11Players
  }

  const renderTeamSelection = (team: "A" | "B") => {
    const teamId = team === "A" ? formData.team_a_id : formData.team_b_id
    const players = team === "A" ? teamAPlayers : teamBPlayers
    const selectedPlayers = team === "A" ? formData.team_a_players : formData.team_b_players
    const teamError = team === "A" ? errors.team_a_players : errors.team_b_players
    const stats = getTeamStats(selectedPlayers)

    return (
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <FiUsers className="w-5 h-5 mr-2" />
          Team {team} Selection
        </h3>

        <div className="mb-4">
          <ModalSelect
            label={`Team ${team}`}
            placeholder={`Select Team ${team}`}
            value={teamId}
            onChange={(option) => {
              setFormData((prev) => ({
                ...prev,
                [`team_${team.toLowerCase()}_id`]: option.value,
                [`team_${team.toLowerCase()}_players`]: [],
              }))
            }}
            fetchOptions={team === "A" ? fetchTeamsForTeamA : fetchTeamsForTeamB}
            required
            searchPlaceholder="Search teams..."
            error={team === "A" ? errors.team_a_id : errors.team_b_id}
            renderOption={(option) => (
              <div>
                <div className="font-medium text-gray-900">{option.label}</div>
                <div className="text-sm text-gray-500 flex items-center mt-0.5">{option.description}</div>
              </div>
            )}
          />
        </div>

        {teamId && (
          <div>
            {/* Team Stats */}
            <div className="mb-4 p-3 bg-white rounded-md border">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="text-center">
                  <p className="font-medium text-gray-900">{stats.total}/12</p>
                  <p className="text-gray-500">Total Players</p>
                </div>
                <div className="text-center">
                  <p className={`font-medium ${stats.captains === 1 ? "text-green-600" : "text-red-600"}`}>
                    {stats.captains}/1
                  </p>
                  <p className="text-gray-500">Captain</p>
                </div>
                <div className="text-center">
                  <p className={`font-medium ${stats.wicketKeepers === 1 ? "text-green-600" : "text-red-600"}`}>
                    {stats.wicketKeepers}/1
                  </p>
                  <p className="text-gray-500">Wicket Keeper</p>
                </div>
                <div className="text-center">
                  <p className={`font-medium ${stats.twelfthMen <= 1 ? "text-green-600" : "text-red-600"}`}>
                    {stats.twelfthMen}/1
                  </p>
                  <p className="text-gray-500">12th Man</p>
                </div>
              </div>
            </div>

            <h4 className="font-medium text-gray-900 mb-3 flex items-center justify-between">
              <span>Select Players ({selectedPlayers.length}/12)</span>
              {stats.total >= 12 && (
                <span className="text-sm text-orange-600 bg-orange-50 px-2 py-1 rounded">Maximum players selected</span>
              )}
            </h4>

            <div className="space-y-3 max-h-80 overflow-y-auto">
              {players.map((player) => {
                const isSelected = selectedPlayers.some((p: Player) => p.player_id === player.id)
                const selectedPlayer = selectedPlayers.find((p: Player) => p.player_id === player.id)
                const canSelectMore = !isPlayerSelectionDisabled(selectedPlayers)
                const isDisabled = !isSelected && !canSelectMore

                return (
                  <div
                    key={player.id}
                    className={`flex items-center justify-between p-3 bg-white rounded-md border transition-all ${isSelected
                      ? "border-primary-300 bg-primary-25 shadow-sm"
                      : isDisabled
                        ? "border-gray-200 bg-gray-50 opacity-60"
                        : "border-gray-200 hover:border-gray-300"
                      }`}
                  >
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => handlePlayerToggle(team, player.id)}
                        className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <div
                        className={`ml-3 ${!isDisabled ? "hover:cursor-pointer" : "cursor-not-allowed"}`}
                        onClick={() => !isDisabled && handlePlayerToggle(team, player.id)}
                      >
                        <p className="font-medium text-gray-900">{player.player_name}</p>
                        <p className="text-sm text-gray-500">
                          #{player.jersey_number} • {getRoleName(player.player_role)}
                        </p>
                        {/* Show selected roles */}
                        {isSelected && (
                          <div className="flex items-center space-x-2 mt-1">
                            {selectedPlayer?.is_captain && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                <FiStar className="w-3 h-3 mr-1" />
                                Captain
                              </span>
                            )}
                            {selectedPlayer?.is_wicket_keeper && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                <FiShield className="w-3 h-3 mr-1" />
                                Wicket Keeper
                              </span>
                            )}
                            {selectedPlayer?.is_12th_man && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                <FiUserX className="w-3 h-3 mr-1" />
                                12th Man
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {isSelected && (
                      <div className="flex items-center space-x-2">
                        {/* Captain Button */}
                        <button
                          type="button"
                          onClick={() => handlePlayerRoleToggle(team, player.id, "captain")}
                          disabled={isCaptainSelectionDisabled(selectedPlayers, player.id)}
                          className={`p-2 rounded-md transition-colors ${selectedPlayer?.is_captain
                            ? "bg-yellow-100 text-yellow-600 border border-yellow-300"
                            : isCaptainSelectionDisabled(selectedPlayers, player.id)
                              ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                              : "bg-gray-100 text-gray-600 hover:bg-yellow-50 hover:text-yellow-600"
                            }`}
                          title={
                            isCaptainSelectionDisabled(selectedPlayers, player.id)
                              ? "Captain already selected"
                              : selectedPlayer?.is_captain
                                ? "Remove as Captain"
                                : "Make Captain"
                          }
                        >
                          <FiStar className="w-4 h-4" />
                        </button>

                        {/* Wicket Keeper Button */}
                        <button
                          type="button"
                          onClick={() => handlePlayerRoleToggle(team, player.id, "wicket_keeper")}
                          disabled={isWicketKeeperSelectionDisabled(selectedPlayers, player.id)}
                          className={`p-2 rounded-md transition-colors ${selectedPlayer?.is_wicket_keeper
                            ? "bg-green-100 text-green-600 border border-green-300"
                            : isWicketKeeperSelectionDisabled(selectedPlayers, player.id)
                              ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                              : "bg-gray-100 text-gray-600 hover:bg-green-50 hover:text-green-600"
                            }`}
                          title={
                            isWicketKeeperSelectionDisabled(selectedPlayers, player.id)
                              ? "Wicket Keeper already selected"
                              : selectedPlayer?.is_wicket_keeper
                                ? "Remove as Wicket Keeper"
                                : "Make Wicket Keeper"
                          }
                        >
                          <FiShield className="w-4 h-4" />
                        </button>

                        {/* 12th Man Checkbox */}
                        <div className="flex items-center space-x-1">
                          <input
                            type="checkbox"
                            id={`12th-man-${team}-${player.id}`}
                            checked={selectedPlayer?.is_12th_man || false}
                            disabled={is12thManSelectionDisabled(selectedPlayers, player.id)}
                            onChange={() => handlePlayerRoleToggle(team, player.id, "12th_man")}
                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <label
                            htmlFor={`12th-man-${team}-${player.id}`}
                            className={`text-sm cursor-pointer ${is12thManSelectionDisabled(selectedPlayers, player.id) ? "text-gray-400 cursor-not-allowed" : "text-gray-700"}`}
                            title={
                              is12thManSelectionDisabled(selectedPlayers, player.id)
                                ? selectedPlayers.length <= 11
                                  ? "12th man only available when more than 11 players selected"
                                  : "12th man already selected"
                                : "Make 12th man"
                            }
                          >
                            12th Man
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Validation Messages */}
            {teamError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{teamError}</p>
              </div>
            )}

            {/* Helpful Messages */}
            {stats.total < 7 && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-600">
                  Select at least {7 - stats.total} more player{7 - stats.total !== 1 ? "s" : ""} to continue
                </p>
              </div>
            )}

            {/* 12th Man Info Message */}
            {stats.total === 11 && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-600">Add 1 more player to enable 12th man selection</p>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  const getRoleName = (roleId: number) => {
    const roles = {
      1: "Batsman",
      2: "Bowler",
      3: "All Rounder",
      4: "Wicket Keeper",
      5: "Spinner",
      6: "Fast Bowler",
      7: "All Rounder",
      8: "Wicket Keeper",
    }
    return roles[roleId as keyof typeof roles] || "Player"
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      const payload = {
        team_a_id: Number.parseInt(formData.team_a_id.toString()),
        team_b_id: Number.parseInt(formData.team_b_id.toString()),
        team_a_players: formData.team_a_players.map((p: Player) => ({
          player_id: p.player_id,
          is_captain: p.is_captain || false,
          is_wicket_keeper: p.is_wicket_keeper || false,
          is_12th_man: p.is_12th_man || false,
        })),
        team_b_players: formData.team_b_players.map((p: Player) => ({
          player_id: p.player_id,
          is_captain: p.is_captain || false,
          is_wicket_keeper: p.is_wicket_keeper || false,
          is_12th_man: p.is_12th_man || false,
        })),
      }

      await api.put(`/matches/${matchData.match_id}/select-players`, payload)
      toast.success("Players selected successfully")
      onComplete(formData)
    } catch (error) {
      console.error("Network Error:", error)
      toast.error("Failed to select players")
    } finally {
      setLoading(false)
    }
  }

  const canSubmit = () => {
    const teamAStats = getTeamStats(formData.team_a_players)
    const teamBStats = getTeamStats(formData.team_b_players)

    return (
      teamAStats.total >= 7 &&
      teamAStats.total <= 12 &&
      teamBStats.total >= 7 &&
      teamBStats.total <= 12 &&
      teamAStats.captains === 1 &&
      teamBStats.captains === 1 &&
      teamAStats.wicketKeepers === 1 &&
      teamBStats.wicketKeepers === 1
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {renderTeamSelection("A")}
        {renderTeamSelection("B")}
      </div>

      <div className="flex justify-between pt-6 border-t">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          Back to Match Setup
        </button>
        <button
          type="submit"
          disabled={loading || !canSubmit()}
          className={`px-6 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors ${canSubmit() && !loading
            ? "bg-primary-600 text-white hover:bg-primary-700"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
        >
          {loading ? "Saving Players..." : "Continue to Match Rules"}
        </button>
      </div>
    </form>
  )
}